import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';
import TipsWindow from '../components/TipsWindow';
import { useTip } from '../hooks/useTips';

// Icons for measurement tools
import StraightenIcon from '@mui/icons-material/Straighten';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory';
import CropFreeIcon from '@mui/icons-material/CropFree';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import UploadIcon from '@mui/icons-material/Upload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

function MeasurementTools({
  MODES,
  measurementMode,
  measurementPoints,
  completedMeasurements,
  highlightedMeasurement,
  handleMeasurementToggle,
  clearMeasurements,
  exportMeasurements,
  handleFileImport,
  deleteMeasurement,
  toggleMeasurementVisibility,
  highlightMeasurement,
  clearMeasurementHighlight
}) {
  const { t } = useTranslation();

  // Tips integration - component is only mounted when panel is open
  const { isOpen: isTipOpen, title: tipTitle, markdownBody: tipBody, close: closeTip } = useTip('measurements', true);

  const buttonStyles = {
    color: '#fff',
    minWidth: '28px',
    width: '28px',
    height: '28px',
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: '18px'
    },
    '&.Mui-selected': {
      color: '#fff',
      backgroundColor: 'rgba(255,255,255,0.3)'
    },
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)'
    }
  };

  return (
    <>
    <TipsWindow
      isOpen={isTipOpen}
      title={tipTitle}
      markdownBody={tipBody}
      onClose={({ doNotShowAgain }) => closeTip(doNotShowAgain)}
    />
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
        {/* Distance Measurement */}
        <ToggleButton
          value="distance"
          selected={measurementMode === MODES.DISTANCE}
          onChange={() => handleMeasurementToggle(MODES.DISTANCE)}
          size="small"
          sx={buttonStyles}
          title={t('DistanceMeasurementTooltip')}
        >
          <StraightenIcon />
        </ToggleButton>

        {/* Height Measurement */}
        <ToggleButton
          value="height"
          selected={measurementMode === MODES.HEIGHT}
          onChange={() => handleMeasurementToggle(MODES.HEIGHT)}
          size="small"
          sx={buttonStyles}
          title={t('HeightMeasurementTooltip')}
        >
          <SquareFootIcon />
        </ToggleButton>

        {/* Angle Measurement */}
        <ToggleButton
          value="angle"
          selected={measurementMode === MODES.ANGLE}
          onChange={() => handleMeasurementToggle(MODES.ANGLE)}
          size="small"
          sx={buttonStyles}
          title={t('AngleMeasurementTooltip')}
        >
          <ChangeHistoryIcon />
        </ToggleButton>

        {/* Area Measurement */}
        <ToggleButton
          value="area"
          selected={measurementMode === MODES.AREA}
          onChange={() => handleMeasurementToggle(MODES.AREA)}
          size="small"
          sx={buttonStyles}
          title={t('AreaMeasurementTooltip')}
        >
          <CropFreeIcon />
        </ToggleButton>

        {/* Circle Measurement */}
        <ToggleButton
          value="circle"
          selected={measurementMode === MODES.CIRCLE}
          onChange={() => handleMeasurementToggle(MODES.CIRCLE)}
          size="small"
          sx={buttonStyles}
          title={t('CircleMeasurementTooltip')}
        >
          <RadioButtonUncheckedIcon />
        </ToggleButton>

        {/* Import Measurements */}
        <ToggleButton
          value="import"
          selected={false}
          component="label"
          size="small"
          sx={{
            ...buttonStyles,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
          title={t('ImportMeasurementsTooltip')}
        >
          <UploadIcon />
          <input
            type="file"
            accept=".json,.geojson"
            onChange={handleFileImport}
            style={{ display: 'none' }}
          />
        </ToggleButton>

        {/* Clear Measurements */}
        <ToggleButton
          value="clear"
          selected={false}
          onChange={clearMeasurements}
          size="small"
          sx={{
            ...buttonStyles,
            '&:hover': {
              backgroundColor: 'rgba(255,100,100,0.3)'
            }
          }}
          title={t('ClearAllMeasurementsTooltip')}
        >
          <DeleteOutlineIcon />
        </ToggleButton>
      </Box>

      {/* Measurement Instructions */}
      {measurementMode !== MODES.NONE && (
        <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1, mt:2 }}>
          <Typography variant="caption" sx={{ color: '#fff' }}>
            {measurementMode === MODES.DISTANCE && t('DistanceInstructions')}
            {measurementMode === MODES.HEIGHT && t('HeightInstructions')}
            {measurementMode === MODES.ANGLE && t('AngleInstructions')}
            {measurementMode === MODES.AREA && t('AreaInstructions')}
            {measurementMode === MODES.CIRCLE && t('CircleInstructions')}
          </Typography>
          {measurementPoints.length > 0 && (
            <Typography variant="caption" sx={{ color: '#eee', display: 'block', mt: 0.5 }}>
              {t('PointsSelected')}: {measurementPoints.length}
              {measurementMode === MODES.CIRCLE && measurementPoints.length === 1 && t('ClickSecondPoint')}
              {measurementMode === MODES.CIRCLE && measurementPoints.length === 2 && t('ClickThirdPoint')}
            </Typography>
          )} 
        </Box>
      )}

      {/* Measurements Section */}
      {completedMeasurements.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#fff', fontSize: '0.75rem' }}>
              {t('Measurements')} {completedMeasurements.length > 0 && `(${completedMeasurements.length})`}
            </Typography>
            <IconButton
              onClick={exportMeasurements}
              sx={{
                color: '#ffffff',
                p: 0.5,
                '&:hover': {
                  bgcolor: 'rgba(182, 182, 182, 0.2)',
                }
              }}
              title={t('ExportMeasurementsTooltip')}
            >
              <SaveIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Box>

          <Box sx={{ 
            maxHeight: '400px', 
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255,255,255,0.1)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255,255,255,0.3)',
              borderRadius: '2px',
            }
          }}>
            {completedMeasurements.map((measurement) => (
              <Box
                key={measurement.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 0.75,
                  mb: 0.5,
                  bgcolor: highlightedMeasurement === measurement.id ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255,255,255,0.05)',
                  borderRadius: 1,
                  border: highlightedMeasurement === measurement.id ? '2px solid rgba(255, 255, 255, 0.8)' : '1px solid rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: highlightedMeasurement === measurement.id ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255,255,255,0.1)',
                  }
                }}
                onClick={(e) => {
                  // Don't trigger on button clicks
                  if (e.target.closest('button')) return;
                  
                  // Toggle highlighting
                  if (highlightedMeasurement === measurement.id) {
                    clearMeasurementHighlight();
                  } else {
                    highlightMeasurement(measurement.id);
                  }
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ 
                    color: highlightedMeasurement === measurement.id ? '#000' : '#fff', 
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: highlightedMeasurement === measurement.id ? 'bold' : 'normal'
                  }}>
                    <Box component="span" sx={{ 
                      pl:1,
                      fontWeight: highlightedMeasurement === measurement.id ? 'bold' : 500, 
                      color: highlightedMeasurement === measurement.id ? '#000' : '#fff' 
                    }}>
                      {t(measurement.type.charAt(0).toUpperCase() + measurement.type.slice(1))}:
                    </Box>
                    {' '}
                    <Box component="span" sx={{ 
                      color: highlightedMeasurement === measurement.id ? '#333' : '#bbb' 
                    }}>
                      {measurement.displayText}
                    </Box>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => toggleMeasurementVisibility(measurement.id)}
                    sx={{
                      color: highlightedMeasurement === measurement.id ? '#000' : '#ffffff',
                      p: 0.5,
                      '&:hover': {
                        bgcolor: 'rgba(182, 182, 182, 0.2)',
                      }
                    }}
                    title={measurement.visible !== false ? t('HideMeasurement') || 'Hide Measurement' : t('ShowMeasurement') || 'Show Measurement'}
                  >
                    {measurement.visible !== false ? (
                      <VisibilityIcon sx={{ fontSize: '12px' }} />
                    ) : (
                      <VisibilityOffIcon sx={{ fontSize: '12px' }} />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => deleteMeasurement(measurement.id)}
                    sx={{
                      color: highlightedMeasurement === measurement.id ? '#000' : '#ffffff',
                      p: 0.5,
                      '&:hover': {
                        bgcolor: 'rgba(255, 100, 100, 0.2)',
                      }
                    }}
                    title={t('DeleteMeasurementTooltip')}
                  >
                    <CloseIcon sx={{ fontSize: '12px' }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
    </>
  );
}

MeasurementTools.propTypes = {
  MODES: PropTypes.object.isRequired,
  measurementMode: PropTypes.string.isRequired,
  measurementPoints: PropTypes.array.isRequired,
  completedMeasurements: PropTypes.array.isRequired,
  highlightedMeasurement: PropTypes.string,
  handleMeasurementToggle: PropTypes.func.isRequired,
  clearMeasurements: PropTypes.func.isRequired,
  exportMeasurements: PropTypes.func.isRequired,
  handleFileImport: PropTypes.func.isRequired,
  deleteMeasurement: PropTypes.func.isRequired,
  toggleMeasurementVisibility: PropTypes.func.isRequired,
  highlightMeasurement: PropTypes.func.isRequired,
  clearMeasurementHighlight: PropTypes.func.isRequired
};

export default MeasurementTools;
